import { FC, useState } from 'react'
import {
  Box, Button, Chip, Collapse, Drawer, Fade, FormControl, IconButton,
  InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography, alpha,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import BoltIcon from '@mui/icons-material/Bolt'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useTranslate } from 'react-admin'
import { FLOW, getCategoryIcon } from './tokens'
import { Registry, TriggerConfig, ActionConfig, TriggerDef, ActionDef, ParamSchema } from './types'

const ParamField: FC<{
  name: string; schema: ParamSchema; value: string | number | boolean
  onChange: (name: string, value: string | number | boolean) => void; accentColor: string
}> = ({ name, schema, value, onChange, accentColor }) => {
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      'borderRadius': '10px',
      'bgcolor': '#fff',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
    },
  }

  if (schema.type === 'select' && schema.options) {
    return (
      <FormControl size="small" fullWidth sx={inputSx}>
        <InputLabel>{schema.label}</InputLabel>
        <Select
          value={value ?? schema.default ?? ''}
          label={schema.label}
          onChange={(e) => onChange(name, e.target.value)}
        >
          {schema.options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  if (schema.type === 'number') {
    return (
      <TextField
        label={schema.label}
        type="number"
        size="small"
        fullWidth
        value={value ?? schema.default ?? ''}
        onChange={(e) => onChange(name, Number(e.target.value))}
        inputProps={{ min: schema.min, max: schema.max }}
        sx={inputSx}
      />
    )
  }

  return (
    <TextField
      label={schema.label}
      size="small"
      fullWidth
      multiline={!!schema.placeholder}
      rows={schema.placeholder ? 2 : 1}
      value={value ?? schema.default ?? ''}
      onChange={(e) => onChange(name, e.target.value)}
      placeholder={schema.placeholder}
      sx={inputSx}
    />
  )
}

interface PickerOptionProps {
  definition: TriggerDef | ActionDef
  category: string
  accentColor: string
  onAdd: (item: TriggerConfig | ActionConfig) => void
  animDelay: number
}

const PickerOption: FC<PickerOptionProps> = ({ definition, category, accentColor, onAdd, animDelay }) => {
  const translate = useTranslate()
  const [params, setParams] = useState<Record<string, string | number | boolean>>(() => {
    const defaults: Record<string, string | number | boolean> = {}
    // eslint-disable-next-line security/detect-object-injection
    for (const [key, schema] of Object.entries(definition.params_schema)) {
      // eslint-disable-next-line security/detect-object-injection
      if (schema.default !== undefined) defaults[key] = schema.default
    }
    return defaults
  })
  const [expanded, setExpanded] = useState(false)

  const handleParamChange = (name: string, value: string | number | boolean) => {
    // eslint-disable-next-line security/detect-object-injection
    setParams((prev) => ({ ...prev, [name]: value }))
  }

  const handleAdd = () => {
    onAdd({ category, type: definition.type, params: { ...params } })
  }

  const paramEntries = Object.entries(definition.params_schema)

  return (
    <Fade in timeout={300} style={{ transitionDelay: `${animDelay}ms` }}>
      <Paper
        elevation={0}
        sx={{
          'borderRadius': '12px',
          'border': `1px solid ${FLOW.grayBorder}`,
          'overflow': 'hidden',
          'transition': 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
          '&:hover': {
            borderColor: alpha(accentColor, 0.3),
            bgcolor: alpha(accentColor, 0.02),
          },
        }}
      >
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            display: 'flex', alignItems: 'center', px: 2, py: 1.75, gap: 1.5,
            cursor: 'pointer', userSelect: 'none',
          }}
        >
          <Box sx={{
            width: 32, height: 32, borderRadius: '8px',
            bgcolor: alpha(accentColor, 0.08),
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {getCategoryIcon(definition.icon || 'tune', { color: accentColor })}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.82)', lineHeight: 1.3 }}>
              {definition.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.45)', lineHeight: 1.3 }}>
              {definition.description}
            </Typography>
          </Box>
          <KeyboardArrowDownIcon sx={{
            color: 'rgba(0,0,0,0.25)', fontSize: 20,
            transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'none',
          }} />
        </Box>

        <Collapse in={expanded}>
          <Box sx={{
            px: 2, pb: 2, pt: 0.5,
            borderTop: `1px solid ${FLOW.grayBorder}`,
            bgcolor: alpha(accentColor, 0.02),
          }}>
            <Box sx={{ mb: 2, mt: 1.5 }}>
              <Typography variant="caption" sx={{
                color: accentColor, fontWeight: 600,
                letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem',
                mb: 1, display: 'block',
              }}>
                {translate('automationRule.picker.configuration')}
              </Typography>
              <Stack spacing={1.5}>
                {paramEntries.map(([name, schema]) => (
                  <ParamField
                    key={name}
                    name={name}
                    schema={schema}
                    // eslint-disable-next-line security/detect-object-injection
                    value={params[name]}
                    onChange={handleParamChange}
                    accentColor={accentColor}
                  />
                ))}
              </Stack>
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={handleAdd}
              startIcon={<AddIcon />}
              disableElevation
              sx={{
                'bgcolor': accentColor,
                'color': '#F2F2F2',
                'borderRadius': '50px',
                'textTransform': 'none',
                'fontWeight': 600,
                'py': 1,
                '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' },
              }}
            >
              {translate('automationRule.picker.addToFlow')}
            </Button>
          </Box>
        </Collapse>
      </Paper>
    </Fade>
  )
}

interface PickerDrawerProps {
  open: boolean
  onClose: () => void
  type: 'trigger' | 'action'
  registry: Registry
  onAdd: (item: TriggerConfig | ActionConfig) => void
}

const PickerDrawer: FC<PickerDrawerProps> = ({ open, onClose, type, registry, onAdd }) => {
  const translate = useTranslate()
  const isTrigger = type === 'trigger'
  const categories = isTrigger ? registry.triggers.categories : registry.actions.categories
  const accentColor = isTrigger ? FLOW.blue : FLOW.green
  const [activeCategory, setActiveCategory] = useState(0)

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 520,
          borderRadius: '20px 0 0 20px',
          border: 'none',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.08)',
        },
      }}
    >
      <Box sx={{
        px: 3, pt: 3, pb: 2,
        borderBottom: `1px solid ${FLOW.grayBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            bgcolor: alpha(accentColor, 0.10),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {isTrigger ? (
              <BoltIcon sx={{ color: accentColor, fontSize: 20 }} />
            ) : (
              <PlayArrowIcon sx={{ color: accentColor, fontSize: 20 }} />
            )}
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {isTrigger ? translate('automationRule.flow.addTrigger') : translate('automationRule.flow.addAction')}
            </Typography>
            <Typography variant="caption" sx={{ color: FLOW.gray }}>
              {isTrigger ? translate('automationRule.picker.triggerHint') : translate('automationRule.picker.actionHint')}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, pt: 2, pb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {categories.map((cat, i) => (
          <Chip
            key={cat.key}
            label={cat.label}
            icon={getCategoryIcon(cat.icon, { fontSize: 16 }) as React.ReactElement}
            onClick={() => setActiveCategory(i)}
            variant={activeCategory === i ? 'filled' : 'outlined'}
            sx={{
              'fontWeight': 600,
              'fontSize': '0.78rem',
              'borderRadius': '8px',
              'height': 34,
              ...(activeCategory === i
                ? { bgcolor: alpha(accentColor, 0.10), color: accentColor, borderColor: 'transparent' }
                : { borderColor: FLOW.grayBorder, color: 'rgba(0,0,0,0.55)' }),
              '&:hover': { bgcolor: alpha(accentColor, 0.08) },
            }}
          />
        ))}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
        <Stack spacing={1.5}>
          {/* eslint-disable-next-line security/detect-object-injection */}
          {categories[activeCategory] && (
            // eslint-disable-next-line security/detect-object-injection
            (isTrigger
              // eslint-disable-next-line security/detect-object-injection
              ? (categories[activeCategory].triggers ?? [])
              // eslint-disable-next-line security/detect-object-injection
              : (categories[activeCategory].actions ?? [])
            ).map((def, i) => (
              <PickerOption
                key={def.type}
                definition={def}
                // eslint-disable-next-line security/detect-object-injection
                category={categories[activeCategory].key}
                accentColor={accentColor}
                onAdd={(item) => { onAdd(item); onClose() }}
                animDelay={i * 60}
              />
            ))
          )}
        </Stack>
      </Box>
    </Drawer>
  )
}

export default PickerDrawer
